import { Analyses, AnalysesNorms, Group, Units, AnalysisContext, Gender, AnalysesFull, Material, MaterialCreate, MaterialUpdate, Specialization, SpecializationCreate, SpecializationUpdate } from '@domain/models';

const runtimeEnv: any = (globalThis as any).window?.__ENV || {};
const API_BASE = (runtimeEnv.API_BASE || process.env.VITE_API_BASE || process.env.API_BASE || '').replace(/\/$/, '');
const API_TOKEN = runtimeEnv.API_TOKEN || process.env.VITE_API_TOKEN || process.env.API_TOKEN || '';

const USE_SSO = typeof window !== 'undefined';

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  let url = `${API_BASE}${path}`;
  if (!USE_SSO && API_TOKEN) {
    url += `${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(API_TOKEN)}`;
  }

  const isDelete = init?.method === 'DELETE';
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> || {}),
  };
  
  if (!isDelete) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    ...init,
    credentials: USE_SSO ? 'include' : 'same-origin', // Для SSO отправляем cookies
    headers,
  });

  if (res.status === 401) {
    if (USE_SSO) {
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `${API_BASE}/api/v1/login?return_url=${returnUrl}`;
    }
    throw new Error(`401 Unauthorized`);
  } else if (res.status === 403) {
    if (USE_SSO) {
      window.location.href = '/access-denied';
    }
    throw new Error(`403 Forbidden`);
  }

  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }

  if (res.status === 204) {
    return null as T;
  }

  return (await res.json()) as T;
}

export const Api = {
  // Analyses
  listAnalyses: () => http<Analyses[]>('/api/v1/analyses/'),
  createAnalyses: (body: Analyses) => http<Analyses>('/api/v1/analyses/', { method: 'POST', body: JSON.stringify(body) }),
  updateAnalyses: (id: number, body: Analyses) => http<Analyses>(`/api/v1/analyses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteAnalyses: (id: number) => http<Analyses>(`/api/v1/analyses/${id}`, { method: 'DELETE' }),
  getAnalysesFullByName: (name: string) => http<AnalysesFull | null>(`/api/v1/analyses/name/${encodeURIComponent(name)}`),
  getAnalysesFullById: (id: number) => http<AnalysesFull | null>(`/api/v1/analyses/${id}`),

  // Norms
  listAllNorms: () => http<AnalysesNorms[]>('/api/v1/norms/'),
  createNorm: (body: AnalysesNorms) => http<AnalysesNorms>('/api/v1/norms/', { method: 'POST', body: JSON.stringify(body) }),
  updateNorm: (id: number, body: AnalysesNorms) => http<AnalysesNorms>(`/api/v1/norms/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteNorm: (id: number) => http<AnalysesNorms>(`/api/v1/norms/${id}`, { method: 'DELETE' }),
  findNormsByName: (name: string) => http<AnalysesNorms[]>(`/api/v1/norms/name/${encodeURIComponent(name)}`),
  findNormsByCode: (code: string) => http<AnalysesNorms[]>(`/api/v1/norms/code/${encodeURIComponent(code)}`),
  getNormsByAnalysisId: (analysisId: number) => http<AnalysesNorms[]>(`/api/v1/norms/by-analysis/${analysisId}`),

  // Groups and genders
  listGroups: () => http<Group[]>('/api/v1/groups/'),
  createGroup: (body: Group) => http<Group>('/api/v1/groups/', { method: 'POST', body: JSON.stringify(body) }),
  updateGroup: (id: number, body: Group) => http<Group>(`/api/v1/groups/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteGroup: (id: number) => http<Group>(`/api/v1/groups/${id}`, { method: 'DELETE' }),
  listGenders: () => http<Gender[]>('/api/v1/genders/genders'),

  // Units & Contexts
  listUnits: () => http<Units[]>('/api/v1/units/'),
  createUnit: (body: Units) => http<Units>('/api/v1/units/', { method: 'POST', body: JSON.stringify(body) }),
  updateUnit: (id: number, body: Units) => http<Units>(`/api/v1/units/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteUnit: (id: number) => http<Units>(`/api/v1/units/${id}`, { method: 'DELETE' }),
  listContexts: () => http<AnalysisContext[]>('/api/v1/contexts/'),
  createContext: (body: AnalysisContext) => http<AnalysisContext>('/api/v1/contexts/', { method: 'POST', body: JSON.stringify(body) }),
  updateContext: (id: number, body: AnalysisContext) => http<AnalysisContext>(`/api/v1/contexts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteContext: (id: number) => http<AnalysisContext>(`/api/v1/contexts/${id}`, { method: 'DELETE' }),

  // Deviations
  listDeviationsByAnalysis: (analysisId: number) => http<any[]>(`/api/v1/deviations/by-analysis/${analysisId}`),
  createDeviation: (body: any) => http<any>('/api/v1/deviations/', { method: 'POST', body: JSON.stringify(body) }),
  updateDeviation: (id: number, body: any) => http<any>(`/api/v1/deviations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteDeviation: (id: number) => http<any>(`/api/v1/deviations/${id}`, { method: 'DELETE' }),

  // Text rules (synonyms)
  listTextRulesByNorm: (analysesNormId: number) => http<any[]>(`/api/v1/text-rules/by-norm/${analysesNormId}`),
  createTextRule: (body: any) => http<any>('/api/v1/text-rules/', { method: 'POST', body: JSON.stringify(body) }),
  updateTextRule: (id: number, body: any) => http<any>(`/api/v1/text-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTextRule: (id: number) => http<any>(`/api/v1/text-rules/${id}`, { method: 'DELETE' }),

  // Materials
  listMaterials: () => http<Material[]>('/api/v1/materials/'),
  createMaterial: (body: MaterialCreate) => http<Material>('/api/v1/materials/', { method: 'POST', body: JSON.stringify(body) }),
  getMaterialById: (id: number) => http<Material>(`/api/v1/materials/${id}`),
  updateMaterial: (id: number, body: MaterialUpdate) => http<Material>(`/api/v1/materials/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteMaterial: (id: number) => http<Material>(`/api/v1/materials/${id}`, { method: 'DELETE' }),

  // Specializations
  listSpecializations: () => http<Specialization[]>('/api/v1/specializations/'),
  createSpecialization: (body: SpecializationCreate) => http<Specialization>('/api/v1/specializations/', { method: 'POST', body: JSON.stringify(body) }),
  getSpecializationById: (id: number) => http<Specialization>(`/api/v1/specializations/${id}`),
  updateSpecialization: (id: number, body: SpecializationUpdate) => http<Specialization>(`/api/v1/specializations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteSpecialization: (id: number) => http<Specialization>(`/api/v1/specializations/${id}`, { method: 'DELETE' }),
};


